import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALL_ACTIONS,
  ALL_ROLES,
  ALL_SECTIONS,
  type PermissionAction,
  type PermissionEntry,
  type PermissionRole,
  type PermissionSection,
} from '@xsc/shared';
import { api } from '../lib/api-client.js';

interface MatrixResponse {
  customer_id: string;
  entries: PermissionEntry[];
}

function key(role: PermissionRole, section: PermissionSection, action: PermissionAction): string {
  return `${role}:${section}:${action}`;
}

export function AdminPermissionsPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<MatrixResponse>({
    queryKey: ['admin', 'permissions'],
    queryFn: () => api.get('/v1/admin/permissions') as Promise<MatrixResponse>,
  });
  const [grid, setGrid] = useState<Record<string, boolean>>({});
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const g: Record<string, boolean> = {};
    for (const e of data.entries) g[key(e.role, e.section, e.action)] = e.allowed;
    setGrid(g);
  }, [data]);

  const save = useMutation({
    mutationFn: (entries: PermissionEntry[]) =>
      api.put('/v1/admin/permissions', { entries }) as Promise<MatrixResponse>,
    onSuccess: () => {
      setSavedAt(Date.now()); setSaveErr(null);
      qc.invalidateQueries({ queryKey: ['admin', 'permissions'] });
      qc.invalidateQueries({ queryKey: ['auth', 'permissions', 'me'] });
    },
    onError: (err) => setSaveErr((err as Error).message),
  });

  if (isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (error) return <p className="text-sm text-danger">{(error as Error).message}</p>;

  const toggle = (role: PermissionRole, section: PermissionSection, action: PermissionAction): void => {
    if (role === 'admin') return; // admin is always all-allowed; UI is read-only
    const k = key(role, section, action);
    setGrid((g) => ({ ...g, [k]: !g[k] }));
  };

  const onSave = (): void => {
    const entries: PermissionEntry[] = [];
    for (const role of ALL_ROLES) {
      for (const section of ALL_SECTIONS) {
        for (const action of ALL_ACTIONS) {
          entries.push({
            role, section, action,
            allowed: role === 'admin' ? true : !!grid[key(role, section, action)],
          });
        }
      }
    }
    save.mutate(entries);
  };

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Permissions</h1>
          <p className="text-sm text-ink-muted">
            Per-section, per-action access for the <code>member</code> and <code>viewer</code> roles.
            Admin always has full access.
          </p>
        </div>
        {savedAt && Date.now() - savedAt < 4000 && (
          <span className="text-sm text-success">Saved ✓</span>
        )}
      </header>

      {saveErr && <p className="text-sm text-danger">{saveErr}</p>}

      {ALL_ROLES.filter((r) => r !== 'admin').map((role) => (
        <section key={role} className="rounded border border-border overflow-hidden">
          <header className="bg-surface-alt px-3 py-2 text-sm font-medium capitalize">
            {role}
          </header>
          <table className="w-full text-sm">
            <thead className="text-xs text-ink-muted">
              <tr>
                <th className="text-left px-3 py-2">Section</th>
                {ALL_ACTIONS.map((a) => (
                  <th key={a} className="text-center px-3 py-2 capitalize">{a}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ALL_SECTIONS.map((section) => (
                <tr key={section} className="border-t border-border">
                  <td className="px-3 py-2 capitalize">{section.replace(/_/g, ' ')}</td>
                  {ALL_ACTIONS.map((action) => {
                    const checked = !!grid[key(role, section, action)];
                    return (
                      <td key={action} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(role, section, action)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      <div className="flex gap-2">
        <button onClick={onSave} disabled={save.isPending}
          className="rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium disabled:opacity-60">
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
